import type {TZDate} from '@date-fns/tz';
import {TZDateMini} from '@date-fns/tz';
import type {CalendarData, Locale} from '@sail/intl';
import {daysOfWeek, maxDate, minDate} from './constants';

export interface DateRange {
  start?: Date;
  end?: Date;
}

export interface TZRange {
  start?: TZDate;
  end?: TZDate;
}

export type YMDDate = `${string}/${string}/${string}`;

export const getPreviousDisplayMonth = (month: number): number => {
  if (month === 0) {
    return 11;
  }
  return month - 1;
};

export const getNextDisplayMonth = (month: number): number => {
  if (month === 11) {
    return 0;
  }
  return month + 1;
};

export const getNextDisplayYear = (month: number, year: number): number => {
  if (month === 11) {
    return year + 1;
  }
  return year;
};

export const getPreviousDisplayYear = (month: number, year: number): number => {
  if (month === 0) {
    return year - 1;
  }
  return year;
};

export const getToday = (timeZone: string) => {
  return new TZDateMini(Date.now(), timeZone);
};

export const getInitialVisibleDate = (
  timeZone: string,
  minDate: TZDate,
  maxDate: TZDate,
  initialVisibleDate?: Date,
) => {
  const date = initialVisibleDate
    ? new TZDateMini(initialVisibleDate, timeZone)
    : getToday(timeZone);

  if (date < minDate) {
    return minDate;
  }

  if (date > maxDate) {
    return maxDate;
  }

  return date;
};

export const getLastYear = (timeZone: string): number => {
  return new TZDateMini(Date.now(), timeZone).getFullYear() - 1;
};

export const getDaysInMonth = (month: number, year: number): number => {
  return new Date(year, month + 1, 0).getDate();
};

export const isSameDay = (date1: TZDate, date2: TZDate): boolean => {
  return (
    (date1.timeZone === date2.timeZone &&
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()) ||
    new Date(
      date1.getFullYear(),
      date1.getMonth(),
      date1.getDate(),
    ).getTime() ===
      new Date(date2.getFullYear(), date2.getMonth(), date2.getDate()).getTime()
  );
};

export const generateCalendarWeeks = (
  year: number,
  month: number,
  timeZone: string,
  countryWeekDays: string[],
): (TZDate | null)[][] => {
  const monthDate = new Date(year, month, 1);
  const daysInMonth = getDaysInMonth(month, year);
  const firstDateWeekdayIndex = countryWeekDays.indexOf(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - Error after enabling noUncheckedIndexedAccess
    daysOfWeek[monthDate.getDay()],
  );

  const calendarWeeks = [];

  // Add nulls for days before the first day of the month
  const firstWeek = new Array(firstDateWeekdayIndex).fill(null);

  // Add days of the month to the weeks
  let week = firstWeek;

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new TZDateMini(year, month, day, timeZone);
    week.push(date);

    // If the week is complete (7 days), push it to the weeks array and start a new week
    if (week.length === 7) {
      calendarWeeks.push(week);
      week = []; // Empty array to start a new week
    }
  }

  // If there are remaining days in the first week, add to calendarWeeks
  if (week.length > 0) {
    // Fill with nulls to complete the week, if needed
    while (week.length < 7) {
      week.push(null);
    }
    calendarWeeks.push(week);
  }

  return calendarWeeks;
};

export const isDateBefore = (
  date: TZDate,
  dateToCompare: Date,
  timeZone: string,
) => {
  const tzDateToCompare = new TZDateMini(dateToCompare, timeZone);
  tzDateToCompare.setHours(0, 0, 0, 0);
  return date.getTime() < tzDateToCompare.getTime();
};

export const isDateAfter = (
  date: TZDate,
  dateToCompare: Date,
  timeZone: string,
) => {
  const tzDateToCompare = new TZDateMini(dateToCompare, timeZone);
  tzDateToCompare.setHours(0, 0, 0, 0);
  return date.getTime() > tzDateToCompare.getTime();
};

export const getDateStringValues = (dateString: YMDDate): number[] => {
  return dateString.split('/').map((s, index) => {
    const dateField = Number(s);
    // Transform month to 0-indexed
    if (index === 1) {
      return dateField - 1;
    }

    return dateField;
  });
};

export const dateStringToTzDate = (
  dateString: YMDDate,
  timeZone: string,
): TZDate => {
  const [year, month, day] = getDateStringValues(dateString);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore - Error after enabling noUncheckedIndexedAccess
  return new TZDateMini(year, month, day, timeZone);
};

const tzDateToDateString = (date: TZDate): YMDDate => {
  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
};

export const getTodayDateString = (timeZone: string): YMDDate => {
  return tzDateToDateString(new TZDateMini(Date.now(), timeZone));
};

export const getMinDate = (
  disableDatesBefore: YMDDate | undefined,
  timeZone: string,
): YMDDate => {
  if (!disableDatesBefore) {
    return minDate;
  }
  const disableBeforeTzDate = dateStringToTzDate(disableDatesBefore, timeZone);
  const minTzDate = dateStringToTzDate(minDate, timeZone);

  return disableBeforeTzDate < minTzDate ? minDate : disableDatesBefore;
};

export const getMaxDate = (
  disableDatesAfter: YMDDate | undefined,
  timeZone: string,
): YMDDate => {
  if (!disableDatesAfter) {
    return maxDate;
  }
  const disableAfterTzDate = dateStringToTzDate(disableDatesAfter, timeZone);
  const maxTzDate = dateStringToTzDate(maxDate, timeZone);

  return disableAfterTzDate > maxTzDate ? maxDate : disableDatesAfter;
};

export const isDateEnabled = (
  date: TZDate,
  minDate: TZDate,
  maxDate: TZDate,
): boolean => {
  return minDate <= date && maxDate >= date;
};

export const isDateRangeEnabled = (
  dateRange: Required<TZRange>,
  timeZone: string,
  minDate: TZDate,
  maxDate: TZDate,
) => {
  return (
    (isSameDay(minDate, dateRange.start) ||
      isDateBefore(minDate, dateRange.start, timeZone)) &&
    (isSameDay(maxDate, dateRange.end) ||
      isDateAfter(maxDate, dateRange.end, timeZone))
  );
};

export const getTZRange = (
  range: DateRange,
  timeZone: string,
): TZRange | undefined => {
  if (!range.start && !range.end) {
    return undefined;
  }

  return {
    start: range.start ? new TZDateMini(range.start, timeZone) : undefined,
    end: range.end ? new TZDateMini(range.end, timeZone) : undefined,
  };
};

export const isSameYearAndMonth = (
  year: number,
  month: number,
  yearToCompare: number | undefined,
  monthToCompare: number | undefined,
) => {
  if (yearToCompare === undefined || monthToCompare === undefined) {
    return false;
  }
  return year === yearToCompare && month === monthToCompare;
};

export const isMonthBeforeDate = (
  year: number,
  month: number,
  dateToCompare: TZDate,
) => {
  if (year < dateToCompare.getFullYear()) {
    return true;
  }

  if (
    year === dateToCompare.getFullYear() &&
    month < dateToCompare.getMonth()
  ) {
    return true;
  }

  return false;
};

export const isMonthAfterDate = (
  year: number,
  month: number,
  dateToCompare: TZDate,
) => {
  if (year > dateToCompare.getFullYear()) {
    return true;
  }

  if (
    year === dateToCompare.getFullYear() &&
    month > dateToCompare.getMonth()
  ) {
    return true;
  }

  return false;
};

export const dateToRange = (value: TZDate | undefined): TZRange | undefined => {
  if (!value) {
    return value;
  }

  return {
    start: value,
    end: value,
  };
};

export function cloneDate(date: TZDate): TZDate {
  return new TZDateMini(date, date.timeZone);
}

function setToBeginningOfDay(date: TZDate): TZDate {
  date.setHours(0, 0, 0, 0);

  return date;
}

function setToEndOfDay(date: TZDate): TZDate {
  date.setHours(23, 59, 59, 999);

  return date;
}

export function toOutputRange(
  range: TZRange | undefined,
): Required<DateRange> | undefined {
  if (!range) {
    return undefined;
  }

  if (range.start && range.end) {
    return {
      start: setToBeginningOfDay(cloneDate(range.start)),
      end: setToEndOfDay(cloneDate(range.end)),
    };
  }

  if (range.start && !range.end) {
    return {
      start: setToBeginningOfDay(cloneDate(range.start)),
      end: setToEndOfDay(cloneDate(range.start)),
    };
  }

  if (range.end && !range.start) {
    return {
      start: setToBeginningOfDay(cloneDate(range.end)),
      end: setToEndOfDay(cloneDate(range.end)),
    };
  }

  return undefined;
}

export function toOutputDate(date: TZDate | undefined): TZDate | undefined {
  if (!date) {
    return undefined;
  }

  return cloneDate(date);
}

export const isInPreviousMonth = (
  date: TZDate,
  currentMonth: number,
  currentYear: number,
) => {
  const previousMonth = getPreviousDisplayMonth(currentMonth);
  const previousYear = getPreviousDisplayYear(currentMonth, currentYear);

  const checkYear = date.getFullYear();
  const checkMonth = date.getMonth();

  return checkMonth === previousMonth && checkYear === previousYear;
};

export const isInNextMonth = (
  date: TZDate,
  currentMonth: number,
  currentYear: number,
) => {
  const nextMonth = getNextDisplayMonth(currentMonth);
  const nextYear = getNextDisplayYear(currentMonth, currentYear);

  const checkMonth = date.getMonth();
  const checkYear = date.getFullYear();

  return checkMonth === nextMonth && checkYear === nextYear;
};

const isInSameMonth = (
  date: TZDate,
  currentMonth: number,
  currentYear: number,
) => {
  const checkYear = date.getFullYear();
  const checkMonth = date.getMonth();

  return checkMonth === currentMonth && checkYear === currentYear;
};

export const isDateInTwoMonthsSpan = (
  date: TZDate,
  currentMonth: number,
  currentYear: number,
) => {
  return (
    isInSameMonth(date, currentMonth, currentYear) ||
    isInNextMonth(date, currentMonth, currentYear)
  );
};

// Current month is displayed as initial month and if it's not between minDate and maxDate
// the component will not have the expected behavior
export const checkTodayIsWithinLimits = (
  minDate: YMDDate | undefined,
  maxDate: YMDDate | undefined,
  timeZone: string,
) => {
  const today = new TZDateMini(new Date(), timeZone);

  if (minDate) {
    const [minYear, minMonth] = getDateStringValues(minDate);
    const tzMinDate = new TZDateMini(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - Error after enabling noUncheckedIndexedAccess
      new Date(minYear, minMonth, 1), // first date of month
      timeZone,
    );
    if (today < tzMinDate) {
      // eslint-disable-next-line no-console
      console.warn(
        `Warning: minDate prop value ${minMonth}/${minYear} is greater than the current month ${
          today.getMonth() + 1
        }/${today.getFullYear()}`,
      );
    }
  }

  if (maxDate) {
    const [maxYear, maxMonth] = getDateStringValues(maxDate);
    const tzMaxDate = new TZDateMini(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - Error after enabling noUncheckedIndexedAccess
      new Date(maxYear, maxMonth + 1, 0), // last date of month
      timeZone,
    );
    if (today > tzMaxDate) {
      // eslint-disable-next-line no-console
      console.warn(
        `Warning: maxDate prop value ${maxMonth}/${maxYear} is smaller than the current month ${
          today.getMonth() + 1
        }/${today.getFullYear()}`,
      );
    }
  }
};

function getCountry(locale: Locale): string {
  return locale.replace(/^([A-Z]+-)/i, '').toUpperCase();
}

export function getFirstDayOfWeek(
  locale: Locale,
  calendarData: CalendarData | undefined,
): number {
  const countryCode = getCountry(locale);
  const firstDay =
    calendarData?.weekData?.firstDay?.[countryCode] || daysOfWeek[0];
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore - Error after enabling noUncheckedIndexedAccess
  const firstDayIndex = Math.max(0, daysOfWeek.indexOf(firstDay));

  return firstDayIndex;
}

export function getWeekDaysForCountry(
  locale: Locale,
  calendarData: CalendarData | undefined,
): string[] {
  const firstDayIndex = getFirstDayOfWeek(locale, calendarData);

  return [
    ...daysOfWeek.slice(firstDayIndex),
    ...daysOfWeek.slice(0, firstDayIndex),
  ];
}

export type WeekInfo = {
  weekNumber: number;
  weekYear: number;
};

/**
 * Get the minimum number of days required in the first week of the year for a locale.
 * @default 1
 */
export function getMinDays(
  locale: Locale,
  calendarData: CalendarData | undefined,
): number {
  const countryCode = getCountry(locale);
  const minDaysFromCLDR =
    calendarData?.weekData?.minDays?.[countryCode] ||
    // `001` is the fallback from CLDR
    calendarData?.weekData?.minDays?.['001'] ||
    // safe fallback, should never happen
    '1';

  return parseInt(minDaysFromCLDR, 10);
}

/**
 * Calculate the start date of week 1 for a given year.
 * Week 1 is determined by the `minDays` rule: the first week that has at least
 * `minDays` days in the new year.
 */
function getFirstWeekStartOfYear(
  year: number,
  timeZone: string | undefined,
  firstDayOfWeek: number,
  minDays: number,
): TZDate {
  // Start with January 1st of the target year
  const jan1 = new TZDateMini(year, 0, 1, timeZone);
  jan1.setHours(0, 0, 0, 0);

  // Calculate how many days Jan 1 is from the start of its week (offset)
  const jan1DayOfWeek = jan1.getDay();
  const daysFromWeekStartOffset = (jan1DayOfWeek - firstDayOfWeek + 7) % 7;

  // Calculate how many days of the week containing Jan 1 are actually in the new year
  const daysOfJan1WeekInNewYear = 7 - daysFromWeekStartOffset;

  // Find the start of the week containing January 1st
  // (this could be in December of the previous year)
  const weekContainingJan1Start = new TZDateMini(
    year,
    0,
    1 - daysFromWeekStartOffset,
    timeZone,
  );
  weekContainingJan1Start.setHours(0, 0, 0, 0);

  // Apply the minDays rule to determine if this week is considered week 1
  if (daysOfJan1WeekInNewYear >= minDays) {
    // The week containing Jan 1 has enough days in the new year to be considered week 1
    return weekContainingJan1Start;
  } else {
    // The week containing Jan 1 doesn't have enough days; week 1 starts in the next week
    const nextWeekStart = new TZDateMini(
      weekContainingJan1Start.getFullYear(),
      weekContainingJan1Start.getMonth(),
      weekContainingJan1Start.getDate() + 7,
      timeZone,
    );
    nextWeekStart.setHours(0, 0, 0, 0);

    return nextWeekStart;
  }
}

/**
 * Get the week number and year for a given date.
 */
export function getWeekNumber(
  date: TZDate,
  locale: Locale,
  calendarData: CalendarData | undefined,
): WeekInfo {
  const firstDayOfWeek = getFirstDayOfWeek(locale, calendarData);
  const minDays = getMinDays(locale, calendarData);

  const workingDate = new TZDateMini(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.timeZone,
  );
  workingDate.setHours(0, 0, 0, 0);

  // Calculate how many days the date is from the start of its week (offset)
  const dateDayOfWeek = workingDate.getDay();
  const daysFromWeekStartOffset = (dateDayOfWeek - firstDayOfWeek + 7) % 7;

  // Find the start of the week containing the input date
  const weekStart = new TZDateMini(
    workingDate.getFullYear(),
    workingDate.getMonth(),
    workingDate.getDate() - daysFromWeekStartOffset,
    date.timeZone,
  );
  weekStart.setHours(0, 0, 0, 0);

  // Get the start of week 1 for the current calendar year
  const currentYear = workingDate.getFullYear();
  const firstWeekStartCurrentYear = getFirstWeekStartOfYear(
    currentYear,
    date.timeZone,
    firstDayOfWeek,
    minDays,
  );

  // Using UTC timestamps here to avoid DST (daylight saving time) issues
  // where a day could have 23 or 25 hours
  const weekStartUTC = Date.UTC(
    weekStart.getFullYear(),
    weekStart.getMonth(),
    weekStart.getDate(),
  );
  const firstWeekStartCurrentYearUTC = Date.UTC(
    firstWeekStartCurrentYear.getFullYear(),
    firstWeekStartCurrentYear.getMonth(),
    firstWeekStartCurrentYear.getDate(),
  );

  // Check if the date's week starts before week 1 of the current year
  // If so, the date belongs to the last week of the previous year
  if (weekStartUTC < firstWeekStartCurrentYearUTC) {
    const lastYearDec31 = new TZDateMini(
      currentYear - 1,
      11,
      31,
      date.timeZone,
    );

    lastYearDec31.setHours(0, 0, 0, 0);

    return getWeekNumber(lastYearDec31, locale, calendarData);
  }

  // Check if the date's week is actually week 1 of the next year
  // (this could happen for dates in late December)
  const firstWeekStartNextYear = getFirstWeekStartOfYear(
    currentYear + 1,
    date.timeZone,
    firstDayOfWeek,
    minDays,
  );
  const firstWeekStartNextYearUTC = Date.UTC(
    firstWeekStartNextYear.getFullYear(),
    firstWeekStartNextYear.getMonth(),
    firstWeekStartNextYear.getDate(),
  );

  if (weekStartUTC >= firstWeekStartNextYearUTC) {
    return {
      weekNumber: 1,
      weekYear: currentYear + 1,
    };
  }

  // Calculate the week number by counting weeks from week 1 start
  const MS_PER_DAY = 86400000;
  const daysDiff = Math.floor(
    (weekStartUTC - firstWeekStartCurrentYearUTC) / MS_PER_DAY,
  );
  const weekNumber = Math.floor(daysDiff / 7) + 1;

  return {
    weekNumber,
    weekYear: currentYear,
  };
}

/**
 * Given a week number and year, return the start date of that week.
 */
export function getDateFromWeekNumber(
  year: number,
  weekNumber: number,
  timeZone: string,
  locale: Locale,
  calendarData: CalendarData | undefined,
): TZDate {
  const firstDayOfWeek = getFirstDayOfWeek(locale, calendarData);
  const minDays = getMinDays(locale, calendarData);

  const firstWeekStart = getFirstWeekStartOfYear(
    year,
    timeZone,
    firstDayOfWeek,
    minDays,
  );

  const targetDate = new TZDateMini(
    firstWeekStart.getFullYear(),
    firstWeekStart.getMonth(),
    firstWeekStart.getDate() + (weekNumber - 1) * 7,
    timeZone,
  );

  return targetDate;
}

/**
 * Get the first day of the week for a given date
 */
export function getStartOfWeek(date: TZDate, firstDayOfWeek = 0): TZDate {
  const dateDayOfWeek = date.getDay();
  const daysFromWeekStart = (dateDayOfWeek - firstDayOfWeek + 7) % 7;

  return new TZDateMini(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() - daysFromWeekStart,
    date.timeZone,
  );
}

/**
 * Get the last day of the week for a given date
 */
export function getEndOfWeek(date: TZDate, firstDayOfWeek = 0): TZDate {
  const startOfWeek = getStartOfWeek(date, firstDayOfWeek);

  return new TZDateMini(
    startOfWeek.getFullYear(),
    startOfWeek.getMonth(),
    startOfWeek.getDate() + 6,
    startOfWeek.timeZone,
  );
}

/**
 * Check if two dates are in the same week
 */
export function isSameWeek(
  date1: TZDate,
  date2: TZDate,
  firstDayOfWeek = 0,
): boolean {
  const weekStart1 = getStartOfWeek(date1, firstDayOfWeek);
  const weekStart2 = getStartOfWeek(date2, firstDayOfWeek);

  return isSameDay(weekStart1, weekStart2);
}

/**
 * Find the first enabled date in a week range based on minDate boundary
 */
export function getFirstEnabledDateInWeek(
  weekStartDate: TZDate,
  weekEndDate: TZDate,
  minDate: TZDate,
): TZDate {
  if (minDate >= weekStartDate && minDate <= weekEndDate) {
    return minDate;
  }

  return weekStartDate;
}

/**
 * Find the last enabled date in a week range based on maxDate boundary
 */
export function getLastEnabledDateInWeek(
  weekStartDate: TZDate,
  weekEndDate: TZDate,
  maxDate: TZDate,
): TZDate {
  if (maxDate >= weekStartDate && maxDate <= weekEndDate) {
    return maxDate;
  }

  return weekEndDate;
}

/**
 * Get a date range in week granularity
 * Contains logic to find the first and last enabled dates within the week boundaries
 */
export function getWeekGranularityRange(
  range: Required<TZRange>,
  firstDayOfWeek: number,
  minDate?: TZDate,
  maxDate?: TZDate,
): Required<TZRange> {
  const weekStart = getStartOfWeek(range.start, firstDayOfWeek);
  const weekEnd = getEndOfWeek(range.end, firstDayOfWeek);

  // If min/max dates are provided, find the first and last enabled dates within the week boundaries
  if (minDate && maxDate) {
    const startDate = getFirstEnabledDateInWeek(
      weekStart,
      getEndOfWeek(range.start, firstDayOfWeek),
      minDate,
    );

    const endDate = getLastEnabledDateInWeek(
      getStartOfWeek(range.end, firstDayOfWeek),
      weekEnd,
      maxDate,
    );

    return {
      start: startDate,
      end: endDate,
    };
  }

  return {
    start: weekStart,
    end: weekEnd,
  };
}

/**
 * Get the first and last enabled dates in a week
 */
export function getEnabledDatesInWeek(
  firstDateInWeek: TZDate,
  firstDayOfWeek: number,
  minDate: TZDate,
  maxDate: TZDate,
): {
  firstEnabledDate: TZDate;
  lastEnabledDate: TZDate;
} {
  const weekStart = getStartOfWeek(firstDateInWeek, firstDayOfWeek);
  const weekEnd = getEndOfWeek(firstDateInWeek, firstDayOfWeek);

  const firstEnabledDate = getFirstEnabledDateInWeek(
    weekStart,
    weekEnd,
    minDate,
  );

  const lastEnabledDate = getLastEnabledDateInWeek(weekStart, weekEnd, maxDate);

  return {
    firstEnabledDate,
    lastEnabledDate,
  };
}

export function getFirstDayOfMonth(
  year: number,
  month: number,
  timeZone: string,
): TZDate {
  return new TZDateMini(year, month, 1, timeZone);
}

export function getLastDayOfMonth(
  year: number,
  month: number,
  timeZone: string,
): TZDate {
  // Day 0 of next month = last day of current month
  return new TZDateMini(year, month + 1, 0, timeZone);
}

/**
 * Check if a date is within a specific month
 */
function isDateInMonth(date: TZDate, year: number, month: number): boolean {
  return date.getFullYear() === year && date.getMonth() === month;
}

/**
 * Get a date range in month granularity
 * Snaps to month boundaries, respecting minDate/maxDate constraints
 */
export function getMonthGranularityRange(
  range: Required<TZRange>,
  minDate?: TZDate,
  maxDate?: TZDate,
): Required<TZRange> {
  const startYear = range.start.getFullYear();
  const startMonth = range.start.getMonth();
  const endYear = range.end.getFullYear();
  const endMonth = range.end.getMonth();
  const timeZone = range.start.timeZone as string;

  let monthStart: TZDate = getFirstDayOfMonth(startYear, startMonth, timeZone);
  let monthEnd: TZDate = getLastDayOfMonth(endYear, endMonth, timeZone);

  // If minDate is within the start month, use minDate instead of first day
  if (minDate && isDateInMonth(minDate, startYear, startMonth)) {
    monthStart = minDate;
  }

  // If maxDate is within the end month, use maxDate instead of last day
  if (maxDate && isDateInMonth(maxDate, endYear, endMonth)) {
    monthEnd = maxDate;
  }

  return {
    start: monthStart,
    end: monthEnd,
  };
}

export function getFirstDayOfYear(year: number, timeZone: string): TZDate {
  return new TZDateMini(year, 0, 1, timeZone);
}

export function getLastDayOfYear(year: number, timeZone: string): TZDate {
  return new TZDateMini(year, 11, 31, timeZone);
}

function isDateInYear(date: TZDate, year: number): boolean {
  return date.getFullYear() === year;
}

/**
 * Get a date range in year granularity
 * Snaps to year boundaries
 */
export function getYearGranularityRange(
  range: Required<TZRange>,
  minDate?: TZDate,
  maxDate?: TZDate,
): Required<TZRange> {
  const startYear = range.start.getFullYear();
  const endYear = range.end.getFullYear();
  const timeZone = range.start.timeZone as string;

  let yearStart: TZDate = getFirstDayOfYear(startYear, timeZone);
  let yearEnd: TZDate = getLastDayOfYear(endYear, timeZone);

  // If minDate is within the start year, use minDate instead of Jan 1
  if (minDate && isDateInYear(minDate, startYear)) {
    yearStart = minDate;
  }

  // If maxDate is within the end year, use maxDate instead of Dec 31
  if (maxDate && isDateInYear(maxDate, endYear)) {
    yearEnd = maxDate;
  }

  return {
    start: yearStart,
    end: yearEnd,
  };
}

/**
 * Returns the maximum number of weeks in a year for a given year and time zone
 */
export function getMaxWeeksInYear(
  year: number,
  timeZone: string,
  locale: Locale,
  calendarData: CalendarData | undefined,
): number {
  const dec31 = new TZDateMini(year, 11, 31, timeZone);
  const {weekNumber, weekYear} = getWeekNumber(dec31, locale, calendarData);

  if (weekYear > year) {
    // Using December 24th because it's guaranteed to be in the current year's last week
    const dec24 = new TZDateMini(year, 11, 24, timeZone);
    return getWeekNumber(dec24, locale, calendarData).weekNumber;
  }

  return weekNumber;
}
